/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
/*
 * Beltton Pods — leasingraden under priset (snippets/pods-lease-line.liquid). Adams beslut 2026-10-05.
 * Räknar om "från"-beloppet och löptidstabellen när varianten byts på produktsidan. Samma regel som
 * Liquid: pris (ören, inkl. 25 % moms) / 125 = kronor exkl. moms, × faktor, avrundat UPPÅT.
 */
if (!window.customElements.get('pods-lease-line')) {
  window.customElements.define('pods-lease-line', class extends HTMLElement {
    connectedCallback() {
      const formId = this.getAttribute('form-id');
      if (!formId) return;
      this._form = document.getElementById(formId);
      this._onVariantChanged = (event) => {
        const variant = event.detail && event.detail.variant;
        if (variant) this._update(variant.price);
      };
      if (this._form) this._form.addEventListener('variant:changed', this._onVariantChanged);
    }

    disconnectedCallback() {
      if (this._form) this._form.removeEventListener('variant:changed', this._onVariantChanged);
    }

    _update(priceCents) {
      const terms = (this.dataset.terms || '').split(',').map((t) => t.split(':'));
      if (!terms.length || !(priceCents > 0)) return;
      const net = priceCents / 125;
      const monthly = (factor) => this._format(Math.ceil(net * Number(factor)));
      const from = this.querySelector('[data-lease-from]');
      if (from) from.textContent = monthly(terms[terms.length - 1][1]);
      terms.forEach(([months, factor]) => {
        const cell = this.querySelector(`[data-lease-term="${months}"]`);
        if (cell) cell.textContent = monthly(factor);
      });
    }

    // Heltal i samma format som Liquid-filtret money_without_trailing_zeros ger för aktuell valuta:
    // snippeten renderar 1 000 000 som exempel (data-money-sample), här byts siffrorna ut.
    _format(amount) {
      const sample = this.dataset.moneySample || '1000000';
      const match = sample.match(/1(\D?)000\1?000/);
      if (!match) return String(amount);
      const grouped = String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, match[1]);
      return sample.replace(match[0], grouped);
    }
  });
}

/*
 * Bås-PDP, mobil: galleriets rutnät visar de 8 första OFILTRERADE bilderna; "Visa alla bilder (N)" fäller ut resten
 * (snippets/product-media.liquid). Liquid sätter .pods-gallery-overflow vid sidladdning; vid variantbyte filtrerar
 * Focal om bilderna (.is-filtered), så klasserna och knappens antal räknas om här. Adam 2026-10-06.
 */
(() => {
  const LIMIT = 8;
  // Ändrar bara klasser som faktiskt ska ändras, och med observern frånkopplad — annars triggar refresh sig själv
  // i en oändlig loop (observern lyssnar på class-ändringar i listan).
  const refresh = (button, list, observer) => {
    if (observer) observer.disconnect();
    const items = Array.from(list.querySelectorAll('.product__thumbnail-item'));
    let visibleCount = 0;
    items.forEach((item) => {
      const filtered = item.classList.contains('is-filtered');
      if (!filtered) visibleCount += 1;
      const overflow = !filtered && visibleCount > LIMIT;
      if (item.classList.contains('pods-gallery-overflow') !== overflow) item.classList.toggle('pods-gallery-overflow', overflow);
    });
    const hide = list.classList.contains('is-expanded') || visibleCount <= LIMIT;
    if (button.hidden !== hide) button.hidden = hide;
    const label = `${button.dataset.label} (${visibleCount})`;
    if (button.textContent !== label) button.textContent = label;
    if (observer) {
      observer.takeRecords();
      observer.observe(list, { subtree: true, attributes: true, attributeFilter: ['class'] });
    }
  };

  const init = (button) => {
    if (button._podsGallery) return;
    button._podsGallery = true;
    const list = button.closest('product-media')?.querySelector('.product__thumbnail-list');
    if (!list) return;
    const observer = new MutationObserver(() => refresh(button, list, observer));
    observer.observe(list, { subtree: true, attributes: true, attributeFilter: ['class'] });
  };

  const setup = () => document.querySelectorAll('[data-pods-gallery-more]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup); else setup();
  document.addEventListener('shopify:section:load', setup);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-pods-gallery-more]');
    if (!button) return;
    const list = button.closest('product-media')?.querySelector('.product__thumbnail-list');
    if (!list) return;
    const firstHidden = list.querySelector('.pods-gallery-overflow');
    list.classList.add('is-expanded');
    button.setAttribute('aria-expanded', 'true');
    button.hidden = true;
    if (firstHidden) firstHidden.focus({ preventScroll: true });
  });
})();
